import { useState, useEffect, useRef } from "react";
import { NavigationItem } from "@/components/ui/navigation/types";
import { serializeNavItems, type SerializedNavItem } from "./nav-customization/serialize";
import { mergeNavCustomization } from "./nav-customization/merge";
import { moveInList } from "./nav-customization/reorder";
import {
  moveChildInItems,
  toggleChildVisibilityInItems,
} from "./nav-customization/child-mutations";
import { logger } from "@/lib/logger";

const NAVIGATION_CUSTOMIZATION_KEY = "navigation-customization";

export function useNavigationCustomization(defaultItems: NavigationItem[]) {
  const [customizedItems, setCustomizedItems] = useState<NavigationItem[]>(defaultItems);
  const [isLoaded, setIsLoaded] = useState(false);
  const initializedRef = useRef(false);
  const defaultItemsRef = useRef(defaultItems);

  // Centralized visibility logic
  const getVisibleItems = () => {
    return customizedItems
      .filter((item) => item.visible !== false) // Show all items unless explicitly hidden
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const getHiddenItems = () => {
    return customizedItems
      .filter((item) => item.visible === false) // Only items explicitly hidden
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const getAllItems = () => {
    return customizedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Initialize on mount and when defaultItems change
  useEffect(() => {
    // Check if defaultItems have actually changed
    const defaultItemsChanged =
      JSON.stringify(defaultItems) !== JSON.stringify(defaultItemsRef.current);

    if (!initializedRef.current || defaultItemsChanged) {
      try {
        const saved = localStorage.getItem(NAVIGATION_CUSTOMIZATION_KEY);
        if (saved) {
          const parsed: SerializedNavItem[] = JSON.parse(saved);
          // Merge saved visibility + order (recursively into children) onto the
          // canonical defaults so submenu customization survives a reload.
          setCustomizedItems(mergeNavCustomization(defaultItems, parsed));
        } else {
          // If no saved preferences, use defaults with all items visible
          setCustomizedItems(defaultItems.map((item) => ({ ...item, visible: true })));
        }
      } catch (error) {
        logger.warn("Failed to load navigation customization", { error });
        // On error, use defaults with all items visible
        setCustomizedItems(defaultItems.map((item) => ({ ...item, visible: true })));
      }

      defaultItemsRef.current = defaultItems;
      initializedRef.current = true;
      setIsLoaded(true);
    }
  }, [defaultItems]);

  // Save customized navigation to localStorage (top-level + child visibility/order)
  const saveCustomization = (items: NavigationItem[]) => {
    try {
      localStorage.setItem(
        NAVIGATION_CUSTOMIZATION_KEY,
        JSON.stringify(serializeNavItems(items))
      );
      setCustomizedItems(items);
    } catch (error) {
      logger.warn("Failed to save navigation customization", { error });
    }
  };

  // Reset to default navigation
  const resetCustomization = () => {
    try {
      localStorage.removeItem(NAVIGATION_CUSTOMIZATION_KEY);

      // Simply make all current default items visible
      const resetItems = defaultItems.map((item) => ({
        ...item,
        visible: true,
      }));

      setCustomizedItems(resetItems);
    } catch (error) {
      logger.warn("Failed to reset navigation customization", { error });
      // Fallback: use current default items
      setCustomizedItems(defaultItems.map((item) => ({ ...item, visible: true })));
    }
  };

  // Update top-level item visibility
  const toggleItemVisibility = (itemId: string) => {
    setCustomizedItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, visible: !item.visible } : item))
    );
  };

  // Move top-level item up in order
  const moveItemUp = (index: number) => {
    setCustomizedItems((prev) => moveInList(prev, index, "up"));
  };

  // Move top-level item down in order
  const moveItemDown = (index: number) => {
    setCustomizedItems((prev) => moveInList(prev, index, "down"));
  };

  // Move a submenu child up within its visibility group
  const moveChildUp = (parentId: string, childId: string) => {
    setCustomizedItems((prev) => moveChildInItems(prev, parentId, childId, "up"));
  };

  // Move a submenu child down within its visibility group
  const moveChildDown = (parentId: string, childId: string) => {
    setCustomizedItems((prev) => moveChildInItems(prev, parentId, childId, "down"));
  };

  // Toggle a submenu child between visible and collapsed-under-"More"
  const toggleChildVisibility = (parentId: string, childId: string) => {
    setCustomizedItems((prev) => toggleChildVisibilityInItems(prev, parentId, childId));
  };

  return {
    // State
    customizedItems,
    isLoaded,

    // Computed values (centralized)
    visibleItems: getVisibleItems(),
    hiddenItems: getHiddenItems(),
    allItems: getAllItems(),

    // Actions — top-level
    saveCustomization,
    resetCustomization,
    toggleItemVisibility,
    moveItemUp,
    moveItemDown,

    // Actions — submenu children
    moveChildUp,
    moveChildDown,
    toggleChildVisibility,
  };
}
